import { Outlet, useOutletContext } from 'react-router-dom'

import { ApolloError } from '@apollo/client'
import { lastDayOfMonth, startOfMonth, subMonths } from 'date-fns'
import {
  InsightsFiltersOptionsDocument,
  InsightsFiltersOptionsQuery,
} from 'generated/resources/graphql'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useQuery } from 'modules/core/hooks/useQuery'
import { dateToString } from 'modules/core/lib/dates'
import users from 'modules/core/store/collections/users'

import { useInsightsFilters } from 'modules/insights/hooks/useInsightsFilters'
import CollectionsBootstrap from 'modules/insights/lib/CollectionsBootstrap'

export type InsightsContext = ReturnType<typeof useInsightsFilters> & {
  filterOptionsData?: InsightsFiltersOptionsQuery
  filterOptionsError?: ApolloError
  users: typeof users
}

export const useInsightsContext = () => {
  return useOutletContext<InsightsContext>()
}

const Container = () => {
  const useInsightsFiltersResult = useInsightsFilters()

  const { data: filterOptionsData, error: filterOptionsError } = useQuery(
    InsightsFiltersOptionsDocument,
    {
      variables: {
        // Taking only the last year values
        fromDate: dateToString(startOfMonth(subMonths(new Date(), 11)), 'backend'),
        toDate: dateToString(lastDayOfMonth(new Date()), 'backend'),
      },
    }
  )

  useAsyncData(() => users.fetch({ withCachedFetchParams: true }), [])

  const canSeeInsights = filterOptionsData?.permissions?._policies.insightsSeeInsights.can ?? false

  return (
    <AuthorizeUser authorizeFn={() => canSeeInsights}>
      <Outlet
        context={
          {
            ...useInsightsFiltersResult,
            filterOptionsData,
            filterOptionsError,
            users,
          } satisfies InsightsContext
        }
      />
    </AuthorizeUser>
  )
}

const RootPage = () => {
  return (
    <CollectionsBootstrap>
      <Container />
    </CollectionsBootstrap>
  )
}

export default RootPage
